import React from 'react';
import { Box, Button, Card, Icon, Link, Text } from '@clariness/clarikit-react';
import {
  ArrowLeft,
  ArrowRightIcon,
  ExternalIcon,
} from '@clariness/clarikit-icons';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRedirect } from 'utils/routing';
import { Routing } from 'global/routing';
import * as ReactRouterDom from 'react-router-dom';
import { captureEventV2 } from 'utils/events/captureEvent';
import { Events } from '@clariness/tracking-event-runtime';
import { Store } from 'store';
import { FAQ } from './components';
import { FAQIcon } from './components/FAQ/FAQIcon';
import { ReportIssueIcon } from './components/ReportIssue/ReportIssueIcon';

export const HelpCenter = () => {
  const { redirect } = useRedirect();
  const { user } = Store.useUser();
  const intl = useIntl();

  const showNavLinks = !window.location.href.endsWith('help-center');
  const faqPath = Routing.FAQ.getPath();
  const reportIssueUrl = `https://clariness.atlassian.net/servicedesk/customer/portal/11/group/33/create/140?customfield_15055=IShelpdesk&email=${user?.email}`;

  return (
    <Box display="flex" width="100%" minWidth="0" maxWidth="100%">
      <Box
        as="nav"
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        width="298px"
        height="100%"
        paddingY="41px"
        backgroundColor="color-gray-variant-6"
        style={{ marginTop: '1px', flexShrink: 0 }}
      >
        <Box paddingX="34px" style={{ marginBottom: '32px' }}>
          <Button
            variant="tertiary"
            iconLeft={ArrowLeft}
            onClick={() => redirect(Routing.HOME.getPath())}
            data-test-id="home-nav-button"
          >
            {intl.formatMessage({
              id: 'is.account.side_bar.back_to_patient',
              defaultMessage: 'Back to patient view',
            })}
          </Button>
        </Box>

        {showNavLinks && (
          <Box width="100%" paddingX="34px" paddingY="18px">
            <Text
              as="span"
              variant="bodyMd"
              color="color-gray-variant-1"
              fontWeight="bold"
              data-test-id="help-center-nav-button"
            >
              <FormattedMessage
                id="is.help_center.side_bar.faq"
                defaultMessage="FAQ"
              />
            </Text>
          </Box>
        )}

        {showNavLinks && (
          <Box width="100%" paddingX="34px" paddingY="12px">
            <Box display="flex" alignItems="center" gap="8" width="fit-content">
              <Link
                url={reportIssueUrl}
                external
                color="color-gray-variant-1"
                data-test-id="help-center-nav-button"
                onClick={() => {
                  captureEventV2(
                    Events.InvestigatorService.InvestigatorReportsIssue({})
                  );
                }}
              >
                <FormattedMessage
                  id="is.help_center.side_bar.report_issue"
                  defaultMessage="Report an issue"
                />
              </Link>
              <Icon
                source={ExternalIcon}
                size="small"
                color="color-gray-variant-1"
                accessibilityLabel=""
              />
            </Box>
          </Box>
        )}
      </Box>

      {!showNavLinks && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="24"
          minWidth="0"
          maxWidth="100%"
          padding="33px 64px"
          style={{ flex: '1 1 0%' }}
        >
          <Text as="h5" variant="headingMd">
            <FormattedMessage
              id="is.help_center.title"
              defaultMessage="Help Center"
            />
          </Text>
          <Box display="flex" flexDirection="column">
            <Text as="p" variant="bodyMd">
              <FormattedMessage
                id="is.help_center.welcome"
                defaultMessage="Welcome to the ClinLife Help Centre!"
              />
            </Text>
            <Text as="p" variant="bodyMd">
              <FormattedMessage
                id="is.help_center.disclaimer"
                defaultMessage="This section is in available in English language only"
              />
            </Text>
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            gap="24"
            width="100%"
            minWidth="0"
          >
            <Card
              style={{
                flex: '0 0 327px',
                width: '327px',
                maxWidth: '100%',
                padding: '24px 24px 32px 24px',
                justifyContent: 'space-between',
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="16"
              >
                <FAQIcon />
                <Text as="h6" variant="headingXs">
                  <FormattedMessage
                    id="is.help_center.faq"
                    defaultMessage="FAQ"
                  />
                </Text>
                <Text as="p" variant="bodyMd" alignment="center">
                  <FormattedMessage
                    id="is.help_center.description"
                    defaultMessage="Find answers to common questions here. Need more help? Contact our Team."
                  />
                </Text>
              </Box>
              <Button
                iconRight={ArrowRightIcon}
                colorVariant="green"
                variant="tertiary"
                accessibilityLabel="FAQ link"
                onClick={() => {
                  captureEventV2(
                    Events.InvestigatorService.InvestigatorOpensFAQ({})
                  );
                  redirect(faqPath);
                }}
              >
                Learn More
              </Button>
            </Card>
            <Card
              style={{
                flex: '0 0 327px',
                width: '327px',
                maxWidth: '100%',
                padding: '24px 24px 32px 24px',
                justifyContent: 'space-between',
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap="16"
              >
                <ReportIssueIcon />
                <Text as="h6" variant="headingXs">
                  <FormattedMessage
                    id="is.help_center.side_bar.report_issue"
                    defaultMessage="Report an issue
"
                  />
                </Text>
                <Text as="p" variant="bodyMd" alignment="center">
                  <FormattedMessage
                    id="is.help_center.report_issue.description"
                    defaultMessage="Report any technical issue you are having with our platfom"
                  />
                </Text>
              </Box>
              <Button
                iconRight={ArrowRightIcon}
                colorVariant="green"
                variant="tertiary"
                accessibilityLabel="FAQ link"
                onClick={() => {
                  captureEventV2(
                    Events.InvestigatorService.InvestigatorReportsIssue({})
                  );
                  window.open(reportIssueUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                Learn More
              </Button>
            </Card>
          </Box>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="center"
        minWidth="0"
        maxWidth="100%"
        paddingY={showNavLinks ? { _: '35px', sm: '90px' } : undefined}
        style={{ flex: showNavLinks ? '1 1 0%' : '0 0 auto' }}
      >
        <ReactRouterDom.Switch>
          <ReactRouterDom.Route path={faqPath} component={FAQ} />
        </ReactRouterDom.Switch>
      </Box>
    </Box>
  );
};
